import { chain, contains, filter, isEmpty, isFunction, map, negate, noop, property, size, values } from 'underscore';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton, PrimaryButton, Panel, PanelType, Stack } from '@fluentui/react';
import { FluentProvider, makeStyles, webLightTheme } from '@fluentui/react-components';
import { SuggestedItemsMultiSelect } from '@bingads-webui-campaign-react/pill-item-components';
import { CopilotTab, CopilotTabItem } from '@bingads-webui/inline-copilot-ui';
import { HelpButton } from '@bingads-webui-react/help-popup';

const useInlineCopilotUIStyles = makeStyles({
  header: {
    display: 'flex',
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '27px',
    justifyContent: 'flex-start',
    paddingLeft: '24px',
    paddingRight: '24px',
    width: '100%',

    '& button': {
      marginLeft: '8px',
    },
  },
  theme: {
    marginTop: '10px',
    marginBottom: '16px',
  },
  summary: {
    marginLeft: '16px',
  },
});

const deriveSuggestedItems = (recommendations, fieldValues) => () =>
  chain(recommendations)
    .map(({ name, assets }) => ({
      assets: map(assets, asset => ({
        key: asset,
        itemText: asset,
        theme: name,
      })),
    }))
    .pluck('assets')
    .flatten()
    .filter(({ itemText }) => !contains(fieldValues, itemText))
    .value();

export const TextAssetRecommendationsWithThemesPanel = ({
  i18n,
  onSave,
  onCancel,
  collection,
  availableRecommendations,
  maxCollectionSize,
  title,
  topicKey,
  topicAriaLabel,
  disabledSuggestionTooltip,
  reportDisplayedRecommendations,
  DKIThemeName,
}) => {
  const [activeTheme, setActiveTheme] = useState(i18n.getString(_TL_('All')));

  const fieldValues = useMemo(() => values(collection), [collection]);
  const [allSuggestedItems, setAllSuggestedItems] = useState(deriveSuggestedItems(availableRecommendations, fieldValues));
  const [suggestedItems, setSuggestedItems] = useState(allSuggestedItems);

  const selectedItemsCount = useMemo(() => size(allSuggestedItems.filter(property('isSelected'))), [allSuggestedItems]);
  const emptyFieldsCount = useMemo(() => maxCollectionSize - size(filter(fieldValues, negate(isEmpty))), [maxCollectionSize, fieldValues]);
  const remaningItemsCount = useMemo(() => emptyFieldsCount - selectedItemsCount, [emptyFieldsCount, selectedItemsCount]);

  const styles = useInlineCopilotUIStyles();

  useEffect(() => {
    if (!isEmpty(availableRecommendations) && isFunction(reportDisplayedRecommendations)) {
      // TODO: Logging
      // reportDisplayedRecommendations(availableRecommendations);
    }
  }, [availableRecommendations, reportDisplayedRecommendations]);

  const onSwitchTheme = useCallback((theme) => {
    setActiveTheme(theme);
    if (theme === i18n.getString(_TL_('All'))) {
      setSuggestedItems(allSuggestedItems);
    } else {
      setSuggestedItems(filter(allSuggestedItems, item => item.theme === theme));
    }
  }, [allSuggestedItems, i18n]);

  const onSuggestedItemToggle = useCallback((itemIndex, itemText) => {
    setSuggestedItems(suggestedItems.map(item => ({
      ...item,
      isSelected: item.itemText === itemText ? !item.isSelected : item.isSelected,
    })));
    setAllSuggestedItems(allSuggestedItems.map(item => ({
      ...item,
      isSelected: item.itemText === itemText ? !item.isSelected : item.isSelected,
    })));
  }, [allSuggestedItems, suggestedItems]);

  const onSaveClick = useCallback(() => {
    onSave(allSuggestedItems.filter(property('isSelected')).map(item => item.itemText));
  }, [allSuggestedItems, onSave]);

  const onCancelClick = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onRenderHeader = useCallback(() => (
    <div className={styles.header}>
      <span>{title}</span>
      { HelpButton && <HelpButton
        topic={topicKey}
        aria-label={topicAriaLabel}
      /> }
    </div>
  ), [styles.header, title, topicAriaLabel, topicKey]);

  const onRenderFooterContent = useCallback(() => (
    <Stack className="text-asset-recommendations-panel-buttons" horizontal tokens={{ childrenGap: 8 }}>
      <Stack.Item key="btn-add">
        <PrimaryButton className="btn-add" onClick={onSaveClick} disabled={!(selectedItemsCount > 0)}>
          {i18n.getString(_TL_('Add'))}
        </PrimaryButton>
      </Stack.Item>
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onCancelClick}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>
      <Stack.Item key="selection-summary" className="selection-summary" align="center">
        <span className={styles.summary}>
          {i18n.getString(_TL_('{{count}} selected'), { count: selectedItemsCount })}
        </span>
      </Stack.Item>
    </Stack>
  ), [onSaveClick, selectedItemsCount, i18n, onCancelClick, styles.summary]);

  return (
    <Panel
      className={classNames('text-asset-recommendations-panel')}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      onDismiss={onCancelClick}
      onOuterClick={noop}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      isOpen
      type={PanelType.medium}
      focusTrapZoneProps={{
        disableFirstFocus: true,
      }}
    >
      <FluentProvider theme={webLightTheme}>
        <div className={styles.theme}>
          <CopilotTab
            active={activeTheme}
            onActiveChange={key => onSwitchTheme(key)}
          >
            <CopilotTabItem tabKey={i18n.getString(_TL_('All'))}>
              {i18n.getString(_TL_('All'))}
            </CopilotTabItem>
            {map(availableRecommendations, ({ name }) => (
              <CopilotTabItem tabKey={name}>
                {name === DKIThemeName ? i18n.getString(DKIThemeName) : name}
              </CopilotTabItem>
            ))}
          </CopilotTab>
        </div>
        <SuggestedItemsMultiSelect
          key={activeTheme}
          suggestedItems={suggestedItems}
          onSuggestedItemToggle={onSuggestedItemToggle}
          disableAllUnselectedSuggestions={!(remaningItemsCount > 0)}
          disabledSuggestionTooltip={disabledSuggestionTooltip}
        />
      </FluentProvider>
    </Panel>
  );
};

TextAssetRecommendationsWithThemesPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  collection: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired,
  availableRecommendations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  maxCollectionSize: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  topicKey: PropTypes.string.isRequired,
  topicAriaLabel: PropTypes.string.isRequired,
  disabledSuggestionTooltip: PropTypes.string.isRequired,
  reportDisplayedRecommendations: PropTypes.func,
  DKIThemeName: PropTypes.string,
};

TextAssetRecommendationsWithThemesPanel.defaultProps = {
  reportDisplayedRecommendations: undefined,
  DKIThemeName: '',
};
